import React, { useState, useEffect, useCallback, useRef } from 'react';
import DynamicFilterPanel, { DynamicFilterConfig } from '../../../components/DynamicComponent/DynamicFilterPanel';
import useApi from '../../../services/useApi';
import dayjs from 'dayjs';
import { getMNOColor } from '../../../utils/chartColors';

// Types for API response
interface MNOTargetData {
  mno_name: string;
  target_amount: number;
  achievement_amount: number;
  achievement_percentage: number;
}

interface MNOTargetSummary {
  total_mnos: number;
  total_target: number;
  total_achievement: number;
  overall_achievement_percentage: number;
}

interface MNOTargetApiResponse {
  data: MNOTargetData[];
  summary: MNOTargetSummary;
}

const MnoTargetAchievement: React.FC = () => {
  const { getAPI } = useApi();
  const hasInitialized = useRef(false);

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [targetData, setTargetData] = useState<MNOTargetData[]>([]);
  // Internal search params for filters (not URL-based)
  const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams());

  // Dynamic filter configuration
  const filterConfig: DynamicFilterConfig = {
    dateRangeDropdown: {
      label: "Duration",
      type: "dateRangeDropdown",
      required: false,
      searchKey: {
        date: "date",
        start: "start_date",
        end: "end_date"
      },
    },
  };

  // Function to fetch MNO target achievement data
  const fetchMNOTargetAchievement = useCallback(async (filters: { [key: string]: string | string[] | boolean | null } = {}) => {
    setLoading(true);
    setError(undefined);

    try {
      // Build query parameters
      const params = new URLSearchParams();

      // Add date range filters
      if (filters.start_date && filters.end_date) {
        params.append('start_date', filters.start_date as string);
        params.append('end_date', filters.end_date as string);
      }

      // Add date filter type
      if (filters.date && filters.date !== '') {
        params.append('date', filters.date as string);
      }

      // Add client timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      params.append('client_timezone', timezone);

      const endpoint = `dashboard/mno-target-achievement${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await getAPI(endpoint);

      if (response.error) {
        setError('Failed to fetch MNO target achievement data');
        return;
      }

      const apiData: MNOTargetApiResponse = response.data;

      // Process the API data
      if (!apiData.data || !Array.isArray(apiData.data)) {
        setTargetData([]);
        return;
      }

      // Filter out MNOs with zero targets and process the data
      const filteredData = apiData.data.filter(item => item.target_amount > 0);

      // The API already provides achievement_percentage, so we can use it directly
      // But we'll round it for display consistency
      const processedData = filteredData.map(item => ({
        ...item,
        achievement_percentage: Math.round(item.achievement_percentage)
      }));

      setTargetData(processedData);

    } catch (err) {
      console.error('Error fetching MNO target achievement:', err);
      setError('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [getAPI]);

  // Handle filter application
  const handleFilterApply = useCallback((filters: { [key: string]: string | string[] | boolean | null }) => {
    // Update internal search params
    const newSearchParams = new URLSearchParams(searchParams);

    // Clear existing filter parameters
    newSearchParams.delete('date');
    newSearchParams.delete('start_date');
    newSearchParams.delete('end_date');

    // Convert dateRangeDropdown filters to the expected format
    const processedFilters: { [key: string]: string | string[] | boolean | null } = {};

    if (filters.date) {
      // Add to internal search params
      newSearchParams.set('date', filters.date as string);

      // Map the date range selection to filter_type
      switch (filters.date) {
        case 'Today':
          processedFilters.date = 'Today';
          break;
        case 'Yesterday':
          processedFilters.date = 'Yesterday';
          break;
        case 'This Month':
          processedFilters.date = 'This Month';
          break;
        case 'This Year':
          processedFilters.date = 'This Year';
          break;
        case 'Custom':
          processedFilters.date = 'Custom Date';
          if (filters.start_date) {
            processedFilters.start_date = dayjs(filters.start_date as string).format('YYYY-MM-DD');
            newSearchParams.set('start_date', filters.start_date as string);
          }
          if (filters.end_date) {
            processedFilters.end_date = dayjs(filters.end_date as string).format('YYYY-MM-DD');
            newSearchParams.set('end_date', filters.end_date as string);
          }
          break;
        default:
          processedFilters.date = 'This Year';
      }
    } else {
      processedFilters.date = 'This Year';
    }

    // Update internal search params
    setSearchParams(newSearchParams);
    fetchMNOTargetAchievement(processedFilters);
  }, [fetchMNOTargetAchievement, searchParams]);

  // Handle filter clear
  const handleFilterClear = useCallback(() => {
    console.log('MNO Target Achievement filters cleared');
    // Clear internal search params
    setSearchParams(new URLSearchParams());
    fetchMNOTargetAchievement({});
  }, [fetchMNOTargetAchievement]);

  // Initial data fetch
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchMNOTargetAchievement();
    }
  }, [fetchMNOTargetAchievement]);

  // Format amount for display
  const formatAmount = (amount: number): string => {
    return amount.toFixed(2);
  };

  // Helper function to convert hex or rgb color to rgba with opacity
  const colorToRgba = (color: string, opacity: number): string => {
    // Handle RGB format
    const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1], 10);
      const g = parseInt(rgbMatch[2], 10);
      const b = parseInt(rgbMatch[3], 10);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Handle hex format
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    // Fallback: return original color if format is not recognized
    return color;
  };

  // Render empty state when no MNOs have targets
  const renderEmptyState = () => (
    <div className="w-full px-4 mb-10">
      <div className="bg-white rounded-lg border border-outline p-8 text-center">
        <div className="text-gray-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Targets Found</h3>
        <p className="text-gray-500">Please add a target for an MNO to view the achievement progress.</p>
      </div>
    </div>
  );

  // Render loading state
  const renderLoadingState = () => (
    <div className="w-full px-4 mb-10 mt-6">
            <h2 className="text-2xl font-semibold -mt-4 mb-6 text-primary_text">MNO Target versus Achievement</h2>
      <div className="bg-white rounded-lg border border-outline p-6">
        <div className="animate-pulse">
          <div className="flex justify-end items-center mb-2">
            {/* <div className="h-10 bg-gray-200 rounded-sm w-48"></div> */}
            <div className="h-10 bg-gray-200 rounded-sm w-20"></div>
          </div>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="mb-6">
              <div className="h-6 bg-gray-200 rounded-sm w-32 mb-2"></div>
              <div className="h-6 bg-gray-200 rounded-sm w-64 mb-2"></div>
              <div className="h-10 bg-gray-200 rounded-sm w-full"></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render error state
  const renderErrorState = () => (
    <div className="w-full px-4 mb-10">
      <div className="bg-white rounded-lg border border-outline p-8 text-center">
        <div className="text-red-400 mb-4">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (loading) {
    return renderLoadingState();
  }

  if (error) {
    return renderErrorState();
  }

  if (!targetData || targetData.length === 0) {
    return renderEmptyState();
  }

  return (
    <div className="w-full px-4 mb-10 ">
      <h2 className="text-2xl font-semibold mt-2 mb-6 text-primary_text">MNO Target versus Achievement</h2>
      <div className="bg-white rounded-lg border border-outline p-6">
        {/* Header */}
        <div className="flex justify-end items-center mb-2">
          <DynamicFilterPanel
            config={filterConfig}
            onApply={handleFilterApply}
            onClear={handleFilterClear}
            loading={loading}
            searchParams={searchParams}
            className="ml-2"
          />
        </div>

        {/* MNO Target Achievement Cards */}
        <div className="">
          {targetData.map((mno) => {
            const color = getMNOColor(mno.mno_name);

            // Calculate display percentage based on business rules
            let displayPercentage = 0;
            let pendingAmount = 0;

            if (mno.achievement_amount === 0) {
              // Rule 1: If achieved amount is 0, show 0%
              displayPercentage = 0;
              pendingAmount = mno.target_amount;
            } else if (mno.target_amount < mno.achievement_amount) {
              // Rule 3: If target_amount < achievement_amount, show 100% and pending as 0
              displayPercentage = 100;
              pendingAmount = 0;
            } else {
              // Normal case: use the achievement_percentage from API and round it
              displayPercentage = Math.round(mno.achievement_percentage);
              pendingAmount = mno.target_amount - mno.achievement_amount;
            }

            return (
              <div key={mno.mno_name} className="rounded-lg py-4">
                {/* MNO Name */}
                <h3 className="text-lg font-medium text-primary_text mb-3">{mno.mno_name}</h3>

                {/* Financial Details */}
                <div className="text-sm font-medium text-gray-600 mb-3">
                  Target: AFA {formatAmount(mno.target_amount)} |
                  Achieved: AFA {formatAmount(mno.achievement_amount)} |
                  Pending: AFA {formatAmount(pendingAmount)}
                </div>

                {/* Progress Bar */}
                <div className="relative">
                  <div className="w-full rounded-lg h-10 overflow-hidden flex relative">
                    {/* Background color - 40% opacity when displayPercentage is 0, lighter otherwise */}
                    {displayPercentage === 0 && (
                      <div
                        className="absolute inset-0 rounded-lg"
                        style={{ backgroundColor: colorToRgba(color, 0.15) }}
                      />
                    )}
                    {/* Achieved portion - full opacity */}
                    {displayPercentage > 0 && (
                      <div
                        className="h-full rounded-lg flex items-center justify-center transition-all duration-300 relative z-10"
                        style={{
                          width: `${Math.min(displayPercentage, 100)}%`,
                          backgroundColor: color
                        }}
                      >
                        <span className="text-white text-sm font-medium">
                          {displayPercentage}%
                        </span>
                      </div>
                    )}
                    {/* Show 0% text when displayPercentage is 0 */}
                    {displayPercentage === 0 && (
                      <div className="absolute inset-0 flex items-center justify-start pl-3 z-10">
                        <span className="text-black text-sm font-medium">0%</span>
                      </div>
                    )}
                    {/* Pending portion - lower opacity (light color) */}
                    {displayPercentage > 0 && displayPercentage < 100 && (
                      <div
                        className="h-full absolute rounded-lg transition-all duration-300"
                        style={{
                          width: `100%`,
                          backgroundColor: colorToRgba(color, 0.15)
                        }}
                      >
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MnoTargetAchievement;
